import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    CircularProgress,
    Alert,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { transliterationApi } from '../services/transliterations';
import { Transliteration, Language } from '../types/transliteration';
import { useConfirm } from '../contexts/ConfirmDialogContext';

const TransliterationsList: React.FC = () => {
    const { projectName, section, chapter, sutraNumber } = useParams<{ projectName: string; section: string; chapter: string; sutraNumber: string }>();
    console.log('Component mounted with params:', { projectName, section, chapter, sutraNumber });
    
    const [transliterations, setTransliterations] = useState<Transliteration[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTransliteration, setEditingTransliteration] = useState<Transliteration | null>(null);
    const [formData, setFormData] = useState<Omit<Transliteration, 'id' | 'sutra_id'>>({
        language: Language.SANSKRIT,
        text: '',
    });
    const navigate = useNavigate();

    const confirm = useConfirm();
  
    const fetchTransliterations = useCallback(async () => {
        if (!projectName || !section || !chapter || !sutraNumber) {
            console.log('Missing required params:', { projectName, section, chapter, sutraNumber });
            return;
        }
        try {
            console.log('Starting to fetch transliterations');
            setLoading(true);
            const data = await transliterationApi.getTransliterations(projectName, section, parseInt(chapter), parseInt(sutraNumber));
            console.log('Fetched transliterations:', data);
            setTransliterations(data);
        } catch (err) {
            console.error('Error in fetchTransliterations:', err);
            setError('Failed to fetch transliterations');
        } finally {
            setLoading(false);
        }
    }, [projectName, section, chapter, sutraNumber]);

    useEffect(() => {
        console.log('useEffect triggered, calling fetchTransliterations');
        fetchTransliterations();
    }, [fetchTransliterations]);

    const handleOpenDialog = (transliteration?: Transliteration) => {
        console.log('Opening dialog with transliteration:', transliteration);
        if (transliteration) {
            setEditingTransliteration(transliteration);
            setFormData({
                language: transliteration.language,
                text: transliteration.text,
            });
        } else {
            setEditingTransliteration(null);
            setFormData({
                language: Language.SANSKRIT,
                text: '',
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        console.log('Closing dialog');
        setOpenDialog(false);
        setEditingTransliteration(null);
    };

    const handleSubmit = async () => {
        if (!projectName || !section || !chapter || !sutraNumber) {
            console.log('Missing required params in handleSubmit:', { projectName, section, chapter, sutraNumber });
            return;
        }
        try {
            console.log('Submitting form data:', formData);
            setLoading(true);
            if (editingTransliteration) {
                console.log('Updating existing transliteration');
                await transliterationApi.updateTransliteration(
                    projectName,
                    section,
                    parseInt(chapter),
                    parseInt(sutraNumber),
                    formData.language,
                    { ...editingTransliteration, ...formData }
                );
            } else {
                console.log('Creating new transliteration');
                await transliterationApi.createTransliteration(
                    projectName,
                    section,
                    parseInt(chapter),
                    parseInt(sutraNumber),
                    formData
                );
            }
            handleCloseDialog();
            fetchTransliterations();
        } catch (err) {
            console.error('Error in handleSubmit:', err);
            setError('Failed to save transliteration');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (transliteration: Transliteration) => {
        const ok = await confirm({
            title: 'Confirm Delete',
            message: `Are you sure you want to delete ...?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });

        if (!ok) return;

        if (!projectName || !section || !chapter || !sutraNumber) {
            console.log('Missing required params in handleDelete:', { projectName, section, chapter, sutraNumber });
            return;
        }
        try {
            console.log('Deleting transliteration:', transliteration);
            setLoading(true);
            await transliterationApi.deleteTransliteration(
                projectName,
                section,
                parseInt(chapter),
                parseInt(sutraNumber),
                transliteration.language
            );
            fetchTransliterations();
        } catch (err) {
            console.error('Error in handleDelete:', err);
            setError('Failed to delete transliteration');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h2 className="text-2xl font-bold mb-4">Transliteration for Sutra {section}/{chapter}/{sutraNumber} in {projectName}</h2>
            <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
                Add New Transliteration
            </Button>

            {loading && <CircularProgress sx={{ mt: 2 }} />}

            {error && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Language</TableCell>
                            <TableCell>Text</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transliterations.map((transliteration) => (
                            <TableRow key={transliteration.id}>
                                <TableCell>{transliteration.language}</TableCell>
                                <TableCell>{transliteration.text}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleOpenDialog(transliteration)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(transliteration)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>
                    {editingTransliteration ? 'Edit Transliteration' : 'Add New Transliteration'}
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Language</InputLabel>
                        <Select
                            value={formData.language}
                            label="Language"
                            onChange={(e) => {
                                console.log('Language changed to:', e.target.value);
                                setFormData({ ...formData, language: e.target.value as Language });
                            }}
                        >
                            {Object.values(Language).map((lang) => (
                                <MenuItem key={lang} value={lang}>
                                    {lang}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="Text"
                        fullWidth
                        multiline
                        rows={4}
                        value={formData.text}
                        onChange={(e) => {
                            console.log('Text changed to:', e.target.value);
                            setFormData({ ...formData, text: e.target.value });
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {editingTransliteration ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default TransliterationsList; 